import type { User } from '@/types/user'
import { paths } from '@/paths'
import { userHasAssistantAccess } from '@/lib/assistant-access'
import {
	AI_SEARCH_TAG,
	BETA_FEATURES_TAG,
	CABINET_HEALTH_TAG,
	CANVA_TAG,
	FOOT_TRAFFIC_TAG,
	GOOGLE_BUSINESS_TAG,
	INCLUDED_TAG,
	INTEGRATIONS_TAG,
	INVENTORY_TAG,
	MARKET_INSIGHTS_TAG,
	PASSCODE_TAG,
	QUICKBOOKS_TAG,
	SHIELD_TAG,
	TOP_DISPLAY_PREVIEW_TAG,
	VDI_TAG,
} from '@/components/dashboard/layout/workspaces/workspaces-context'

import { canAccessEnterpriseApiSettings, canAccessEnterpriseMcpSettings } from './enterprise-api'
import { Role } from './role'

export function hasQuickBooksAccess(user?: User | null): boolean {
	return Boolean(user?.tags?.includes(QUICKBOOKS_TAG))
}

export function hasCanvaAccess(user?: User | null): boolean {
	return Boolean(user?.tags?.includes(CANVA_TAG))
}

export function hasGoogleBusinessAccess(user?: User | null): boolean {
	return Boolean(user?.tags?.includes(GOOGLE_BUSINESS_TAG))
}

export function hasVdiAccess(user?: User | null): boolean {
	return Boolean(user?.tags?.includes(VDI_TAG))
}

export function hasAiSearchAccess(user?: User | null): boolean {
	return Boolean(user?.tags?.includes(AI_SEARCH_TAG))
}

export function hasShieldAccess(user?: User | null): boolean {
	return Boolean(user?.tags?.includes(SHIELD_TAG))
}

export function hasIncludedAccess(user?: User | null): boolean {
	return Boolean(user?.tags?.includes(INCLUDED_TAG))
}

export function hasIncludedAssistantAccess(user?: User | null): boolean {
	return hasIncludedAccess(user) && userHasAssistantAccess(user)
}

// Pages that require beta access
export const betaPages = [paths.dashboard.copilot.list]

// Accessible pages for restricted roles
export const restrictedRolePermissions = {
	[Role.Restocker]: [
		paths.dashboard.restocks.list,
		paths.dashboard.restocks.details,
		paths.dashboard.restocks.new,
		paths.dashboard.restocks.chooseCabinet,
		paths.dashboard.restocks.restockCabinet,
		paths.dashboard.restocks.restockCabinetSummary,
		paths.dashboard.settings.account,
		paths.dashboard.analytics.inventory,
		paths.dashboard.stores.list,
		paths.dashboard.stores.details,
		paths.dashboard.stores.cabinets.details,
		paths.dashboard.stores.cabinets.configurePriceTags,
		paths.dashboard.stores.cabinets.planogram.create,
		paths.dashboard.stores.cabinets.planogram.edit,
		paths.dashboard.health,
		paths.dashboard.settings.notifications,
		paths.learn,
	],
} as any

const checkBetaAccess = (user: User, currentPath: string): boolean => {
	const requiresBetaAccess = betaPages.some((path) => currentPath.startsWith(path))
	if (requiresBetaAccess) {
		const hasBetaAccess = user?.tags?.includes(BETA_FEATURES_TAG)
		if (!hasBetaAccess) {
			return true // restrict access if beta access is required but user doesn't have it
		}
	}
	return false
}

const checkInventoryAccess = (user: User, currentPath: string): boolean => {
	if (currentPath.startsWith(paths.dashboard.inventory.list)) {
		return !user?.tags?.includes(INVENTORY_TAG)
	}
	return false
}

const checkCabinetsAccess = (user: User, currentPath: string): boolean => {
	if (currentPath.startsWith(paths.dashboard.settings.cabinets)) {
		return !user?.tags?.includes(PASSCODE_TAG)
	}
	return false
}

const LEGACY_API_PATH = '/dashboard/settings/api'

const checkEnterpriseApiAccess = (user: User, currentPath: string): boolean => {
	if (currentPath.startsWith(paths.dashboard.settings.api) || currentPath.startsWith(LEGACY_API_PATH)) {
		return !canAccessEnterpriseApiSettings(user)
	}
	return false
}

const checkEnterpriseMcpAccess = (user: User, currentPath: string): boolean => {
	if (currentPath.startsWith(paths.dashboard.settings.mcp)) {
		return !canAccessEnterpriseMcpSettings(user)
	}
	return false
}

const checkIntegrationsAccess = (user: User, currentPath: string): boolean => {
	// Nested integration detail routes carry their own per-integration gates.
	if (currentPath.startsWith(paths.dashboard.settings.assistant)) {
		return !hasIncludedAssistantAccess(user)
	}
	const includedPaths = [
		paths.dashboard.settings.revenueProtect,
		paths.dashboard.settings.guardian,
		paths.dashboard.settings.shield,
		paths.dashboard.settings.interac,
		paths.dashboard.settings.footTraffic,
	]
	if (includedPaths.some((path) => currentPath.startsWith(path))) {
		return !hasIncludedAccess(user)
	}
	if (currentPath.startsWith(paths.dashboard.settings.quickbooks)) {
		return !hasQuickBooksAccess(user)
	}
	if (currentPath.startsWith(paths.dashboard.settings.googleBusinessProfile)) {
		return !hasGoogleBusinessAccess(user)
	}
	if (currentPath.startsWith(paths.dashboard.settings.aiSearch)) {
		return !hasAiSearchAccess(user)
	}
	if (currentPath.startsWith(paths.dashboard.settings.api) || currentPath.startsWith(paths.dashboard.settings.mcp)) {
		// Gated by checkEnterpriseApiAccess / checkEnterpriseMcpAccess instead.
		return false
	}
	if (currentPath.startsWith(paths.dashboard.settings.integrations)) {
		return !user?.tags?.includes(INTEGRATIONS_TAG)
	}
	return false
}

const checkMarketInsightsAccess = (user: User, currentPath: string): boolean => {
	if (currentPath.startsWith(paths.dashboard.marketInsights.overview)) {
		return !user?.tags?.includes(MARKET_INSIGHTS_TAG)
	}
	return false
}

const checkFootTrafficAccess = (user: User, currentPath: string): boolean => {
	if (currentPath.startsWith(paths.dashboard.locationInsights.overview)) {
		return !user?.tags?.includes(FOOT_TRAFFIC_TAG)
	}
	return false
}

const checkHealthAccess = (user: User, currentPath: string): boolean => {
	if (
		currentPath.startsWith(paths.dashboard.health) ||
		currentPath.startsWith(paths.dashboard.settings.notifications)
	) {
		return !user?.tags?.includes(CABINET_HEALTH_TAG)
	}
	return false
}

const checkCanvaAccess = (user: User, currentPath: string): boolean => {
	if (
		currentPath.startsWith(paths.dashboard.marketing.canva) ||
		currentPath.startsWith(paths.dashboard.settings.canva)
	) {
		return !user?.tags?.includes(CANVA_TAG)
	}
	return false
}

const checkTopDisplayPreviewAccess = (user: User, currentPath: string): boolean => {
	if (/^\/dashboard\/stores\/[^/]+\/top-display-preview(?:\/|$)/.test(currentPath)) {
		return !user?.tags?.includes(TOP_DISPLAY_PREVIEW_TAG)
	}
	return false
}

export const isRestrictedPath = (user: User, currentPath: string): boolean => {
	// Check beta access first
	const isRestrictedByBetaAccess = checkBetaAccess(user, currentPath)
	if (isRestrictedByBetaAccess) {
		return true
	}

	if (checkInventoryAccess(user, currentPath)) {
		return true
	}

	if (checkCabinetsAccess(user, currentPath)) {
		return true
	}

	if (checkEnterpriseApiAccess(user, currentPath)) {
		return true
	}

	if (checkEnterpriseMcpAccess(user, currentPath)) {
		return true
	}

	if (checkIntegrationsAccess(user, currentPath)) {
		return true
	}

	if (checkMarketInsightsAccess(user, currentPath)) {
		return true
	}

	if (checkFootTrafficAccess(user, currentPath)) {
		return true
	}

	if (checkHealthAccess(user, currentPath)) {
		return true
	}

	if (checkCanvaAccess(user, currentPath)) {
		return true
	}

	if (checkTopDisplayPreviewAccess(user, currentPath)) {
		return true
	}

	// Admins have full access
	if (user.role === Role.Admin) {
		return false
	}

	// If the user is a Restocker, allow only specific paths
	if (user.role === Role.Restocker) {
		const isAllowed = restrictedRolePermissions[user.role]?.some((allowedPath) => currentPath.startsWith(allowedPath))
		return !isAllowed
	}

	return true // block unknown roles by default
}
